import { useMemo, useState, useEffect } from 'react';
import Img from '../components/ui/Img';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import {
  User,
  Mail,
  Phone,
  LogOut,
  HelpCircle,
  ChevronRight,
  CreditCard,
  Repeat,
  Plus,
  Info,
  ChevronDown,
  Tag,
  MoreHorizontal,
  Star,
  Trash2,
  Pencil,
  Check,
  X as XIcon,
} from 'lucide-react';
import { PaymentMethodRow } from '../components/PaymentMethodRow';
import { describeSavedCard, type PaymentMethodDisplay } from '../utils/paymentMethodDisplay';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent, analytics } from '../utils/analytics';
import { LEGAL_ENTITY_NAME } from '../config/brandProfiles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { ParentPaymentPreferences } from '../types/payment-preferences';
import { VERSION_INFO } from '../config/version';
import { formatDateInBusinessTZ } from '../utils/date';
import { useLabels } from '../config/useLabels';
import { formatPhoneCanonical, handlePhoneInput, isValidPhoneOrEmpty } from '../utils/phone';
import { AccountAddressRow } from '../components/AccountAddressRow';

interface BankSetupView {
  id: string;
  bank_name?: string | null;
  account_type?: string | null;
  last4?: string | null;
  status?: string | null;       // e.g. 'succeeded', 'requires_payment_method', etc.
  is_active?: boolean | null;
}

export function Account() {
  const { user, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { hash } = useLocation();

  /* A client-side route never fires the browser's own fragment scroll, so `/account#…` lands
     at the top of a long page. The dashboard's Auto Pay shortcut points at the payment
     settings block, and this is what makes it arrive there. */
  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    if (target) target.scrollIntoView({ block: 'start' });
  }, [hash]);
  const t = useLabels();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [autoPayEnabled, setAutoPayEnabled] = useState(false);
  const [isAboutCollapsed, setIsAboutCollapsed] = useState(true);
  const [autoPayNotice, setAutoPayNotice] = useState<string | null>(null);
  const [paymentMethodNotice, setPaymentMethodNotice] = useState<string | null>(null);
  // Tracks which payment-method row has its actions menu open (null = none)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Payment methods are derived from data; no local state needed

  const queryClient = useQueryClient();

  // Fetch parent id
  const { data: parent, error: parentError } = useQuery({
    queryKey: ['parent-id', user?.email],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parents')
        .select('id, phone, first_name, last_name')
        .eq('email', user!.email!)
        .single();

      if (error && error.code === 'PGRST116') {
        // No rows returned - parent not found
        throw new Error('Parent not found');
      }

      if (error) throw error;
      return data as { id: string; phone: string | null; first_name: string; last_name: string };
    },
    enabled: !!user?.email,
    retry: (failureCount, error) => {
      // Don't retry if parent not found
      if (error.message === 'Parent not found') {
        return false;
      }
      return failureCount < 3;
    }
  });

  // moved: last-used query is defined after bank/card queries

  // (moved) last-used method query is defined after bank/card queries

  // Handle parent not found error with useEffect
  useEffect(() => {
    if (parentError && parentError.message === 'Parent not found') {
      navigate('/dashboard', { replace: true });
    }
  }, [parentError, navigate]);

  // Fetch payment preferences
  const { data: prefs, isLoading: prefsLoading, isFetching: prefsFetching } = useQuery({
    queryKey: ['parent-payment-prefs', parent?.id],
    queryFn: async () => {
      if (!parent?.id) return null;
      const { data, error } = await supabase
        .from('parent_payment_preferences')
        .select('*')
        .eq('parent_id', parent.id)
        .maybeSingle();
      if (error) throw error;
      return (data as ParentPaymentPreferences) || null;
    },
    enabled: !!parent?.id,
    staleTime: 0
  });

  // Fetch active saved card (if any)
  const { data: activeCard, isLoading: cardLoading, isFetching: cardFetching } = useQuery({
    queryKey: ['card-pm', parent?.id],
    queryFn: async () => {
      if (!parent?.id) return null;
      const { data, error } = await supabase
        .from('card_payment_methods')
        .select('id, brand, last4, exp_month, exp_year, is_active')
        .eq('parent_id', parent.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .maybeSingle();
      if (error) throw error;
      return data as {
        id: string;
        brand: string | null;
        last4: string | null;
        exp_month: number | null;
        exp_year: number | null;
        is_active: boolean;
      } | null;
    },
    enabled: !!parent?.id
  });

  // Fetch active/default bank account setup for display
  const { data: bankSetup, isLoading: bankLoading, isFetching: bankFetching } = useQuery({
    queryKey: ['bank-account-setup', parent?.id, prefs?.default_bank_account_setup_intent_id],
    queryFn: async () => {
      if (!parent?.id) return null;
      const parentIdStr = String(parent.id);
      const selectCols = 'id, bank_name, account_type, last4, status, is_active, parent_id';
      const makeBase = () => supabase
        .from('bank_account_setup_intents')
        .select(selectCols);

      if (prefs?.default_bank_account_setup_intent_id) {
        // If we have a stored reference, show it ONLY if it's still active
        const { data, error } = await makeBase()
          .eq('id', prefs.default_bank_account_setup_intent_id)
          .eq('is_active', true)
          .maybeSingle();
        if (error) throw error;
        if (data) {
          return (data as BankSetupView) || null;
        }
        // Fallback: show the latest active succeeded intent (verified), else latest active any status (pending/failed).
        const { data: latestSucceeded } = await makeBase()
          .eq('parent_id', parentIdStr)
          .eq('is_active', true)
          .eq('status', 'succeeded')
          .order('created_at', { ascending: false })
          .maybeSingle();
        if (latestSucceeded) return (latestSucceeded as BankSetupView) || null;

        const { data: latestActiveAny } = await makeBase()
          .eq('parent_id', parentIdStr)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .maybeSingle();
        return (latestActiveAny as BankSetupView) || null;
      }
      // No default link; show the most recent regardless of active flag to aid discovery,
      // but prefer active/succeeded first. Never show inactive (removed) bank accounts.
      const { data, error } = await makeBase()
        .eq('parent_id', parentIdStr)
        .eq('is_active', true)
        .eq('status', 'succeeded')
        .order('created_at', { ascending: false })
        .maybeSingle();
      if (error) throw error;
      if (data) {
        return (data as BankSetupView) || null;
      }
      // Last resort: show the latest ACTIVE setup even if not succeeded (e.g. verification pending).
      const { data: latestActiveAny } = await makeBase()
        .eq('parent_id', parentIdStr)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .maybeSingle();
      return (latestActiveAny as BankSetupView) || null;
    },
    enabled: !!parent?.id,
    staleTime: 0,
    refetchOnMount: 'always'
  });

  // Keep local toggle in sync with prefs
  useEffect(() => {
    if (prefs) setAutoPayEnabled(prefs.autopay_enabled);
  }, [prefs]);

  // Enforce: if only Zelle is available (no active bank/card) or default is Zelle,
  // autopay must be OFF. If default is bank and we find an active setup intent,
  // re-point to it and keep autopay ON.
  useEffect(() => {
    (async () => {
      if (!prefs) return;
      // Avoid enforcement while queries are still loading to prevent transient turn-off
      const queriesLoading = prefsLoading || prefsFetching || bankLoading || bankFetching || cardLoading || cardFetching;
      if (queriesLoading) return;
      const defaultIsZelle = prefs.default_payment_method === 'zelle';
      const defaultIsBank = prefs.default_payment_method === 'bank_account';
      const hasCapableBank = !!bankSetup && bankSetup.is_active === true && bankSetup.status === 'succeeded';
      const noAutoPayCapableMethod = !activeCard && !hasCapableBank;

      // If autopay is ON and default method is bank but the stored default setup intent
      // is missing or differs from the currently active succeeded intent, automatically
      // re-point to the active bank account and keep autopay ON.
      if (prefs.autopay_enabled && defaultIsBank && hasCapableBank && bankSetup?.id && prefs.default_bank_account_setup_intent_id !== bankSetup.id) {
        try {
          await supabase.rpc('upsert_parent_payment_preferences', {
            p_default_payment_method: 'bank_account',
            p_autopay_enabled: true,
            p_default_bank_account_setup_intent_id: bankSetup.id,
          });
          setAutoPayEnabled(true);
          queryClient.invalidateQueries({ queryKey: ['parent-payment-prefs'] });
          return;
        } catch (_) {
          // If we cannot update, fall through to enforcement below
        }
      }

      // If default is bank but bank is not verified/active and no active card, force turn off autopay
      if (prefs.autopay_enabled && defaultIsBank && !hasCapableBank && !activeCard) {
        setAutoPayEnabled(false);
        setAutoPayNotice('Auto Pay was turned off because your bank account is not verified yet.');
        try {
          await supabase.rpc('upsert_parent_payment_preferences', {
            p_default_payment_method: 'bank_account',
            p_autopay_enabled: false,
            p_default_bank_account_setup_intent_id: prefs.default_bank_account_setup_intent_id ?? null,
          });
          queryClient.invalidateQueries({ queryKey: ['parent-payment-prefs'] });
        } catch (_) {
          // ignore in UI; enforcement is local if network fails
        }
      }

      if (prefs.autopay_enabled && (defaultIsZelle || noAutoPayCapableMethod)) {
        setAutoPayEnabled(false);
        setAutoPayNotice('Auto Pay was turned off because Zelle does not support autopay.');
        try {
          await supabase.rpc('upsert_parent_payment_preferences', {
            p_default_payment_method: null,
            p_autopay_enabled: false,
            p_default_bank_account_setup_intent_id: null,
          });
          queryClient.invalidateQueries({ queryKey: ['parent-payment-prefs'] });
        } catch (_) {
          // ignore in UI; enforcement is local if network fails
        }
      }
    })();
  }, [prefs, activeCard, bankSetup, queryClient, prefsLoading, prefsFetching, bankLoading, bankFetching, cardLoading, cardFetching]);

  // Fetch last-used payment timestamps per method for this parent (scoped by active card/bank)
  type PaymentLastUsedRaw = {
    payment_method: 'card' | 'ach' | 'zelle' | 'credit_card' | 'cash' | 'check' | string;
    payment_date?: string | null;
    created_at?: string | null;
    bank_account_setup_intent_id?: string | null;
    stripe_transactions?: Array<{ payment_method_details?: Record<string, unknown> | null; created_at?: string | null }>;
  };
  type LastUsedMap = Partial<Record<'bank_account' | 'card' | 'zelle', string>>;
  const { data: lastUsedByMethod } = useQuery<LastUsedMap>({
    queryKey: ['last-used-methods', parent?.id, bankSetup?.id, activeCard?.last4],
    queryFn: async () => {
      if (!parent?.id) return {};
      // Fetch student ids for this parent (payer scope)
      const { data: studentParents, error: spErr } = await supabase
        .from('student_parents')
        .select('student_id')
        .eq('parent_id', parent.id)
        .eq('can_pay', true);
      if (spErr) throw spErr;
      const studentIds = (studentParents as Array<{ student_id: string }> | null)?.map(s => s.student_id) ?? [];
      if (studentIds.length === 0) return {};
      // Query recent completed payments scoped to those students
      const { data, error } = await supabase
        .from('payments')
        .select(`
          payment_method,
          payment_date,
          created_at,
          bank_account_setup_intent_id,
          stripe_transactions (
            payment_method_details,
            created_at
          ),
          invoice:invoices!inner (
            id,
            enrollment:enrollments!inner (
              id
            )
          )
        `)
        .in('invoice.enrollment.student_id', studentIds)
        .eq('status', 'completed')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = (data as unknown as PaymentLastUsedRaw[]) ?? [];
      const map: LastUsedMap = {};
      // ACH: match the active bank account specifically by setup_intent_id
      if (bankSetup?.id) {
        const achRows = rows.filter(r => r.payment_method === 'ach' && r.bank_account_setup_intent_id === bankSetup.id);
        const achLatest = achRows.reduce<string | null>((acc, r) => {
          const d = r.payment_date || r.created_at || null;
          if (!d) return acc;
          if (!acc || new Date(d).getTime() > new Date(acc).getTime()) return d;
          return acc;
        }, null);
        if (achLatest) map.bank_account = achLatest;
      }
      // Card: match by last4 (simple + good enough)
      if (activeCard?.last4) {
        const cardRows = rows.filter(r => r.payment_method === 'card' || r.payment_method === 'credit_card');
        let latest: string | null = null;
        for (const r of cardRows) {
          const txs = r.stripe_transactions ?? [];
          const hasMatch = txs.some(tx => {
            const details = (tx.payment_method_details ?? {}) as {
              card?: { last4?: string | null; brand?: string | null };
            };
            const txLast4 = details?.card?.last4 ?? null;
            return txLast4 && activeCard.last4 && String(txLast4) === String(activeCard.last4);
          });
          if (!hasMatch) continue;
          const d = r.payment_date || r.created_at || null;
          if (!d) continue;
          if (!latest || new Date(d).getTime() > new Date(latest).getTime()) {
            latest = d;
          }
        }
        if (latest) map.card = latest;
      }
      // Zelle: latest across method
      {
        const zRows = rows.filter(r => r.payment_method === 'zelle');
        const zLatest = zRows.reduce<string | null>((acc, r) => {
          const d = r.payment_date || r.created_at || null;
          if (!d) return acc;
          if (!acc || new Date(d).getTime() > new Date(acc).getTime()) return d;
          return acc;
        }, null);
        if (zLatest) map.zelle = zLatest;
      }
      return map;
    },
    enabled: !!parent?.id,
    staleTime: 0
  });

  // Derive display list of payment methods (bank account + zelle)
  const paymentMethods: PaymentMethodDisplay[] = useMemo(() => {
    const methods: PaymentMethodDisplay[] = [];
    if (bankSetup) {
      const bankIsVerified = bankSetup.is_active === true && bankSetup.status === 'succeeded';
      methods.push({
        id: bankSetup.id,
        type: 'bank_account',
        name: `${bankSetup.bank_name ?? 'Bank'} •••• ${bankSetup.last4 ?? '••••'}`,
        isDefault: prefs?.default_payment_method === 'bank_account',
        lastUsed: lastUsedByMethod?.bank_account ?? null,
        bankVerificationStatus: bankIsVerified ? 'verified' : 'pending',
      });
    }
    // Only show Card row when a saved active card exists
    if (activeCard) {
      methods.push({
        id: activeCard.id,
        type: 'card',
        ...describeSavedCard(activeCard),
        isDefault: prefs?.default_payment_method === 'card',
        lastUsed: lastUsedByMethod?.card ?? null,
      });
    }
    methods.push({
      id: 'zelle-default',
      type: 'zelle',
      name: 'Zelle',
      detail: 'billing@bhcskids.com',
      isDefault: prefs?.default_payment_method === 'zelle',
      lastUsed: lastUsedByMethod?.zelle ?? null,
    });
    return methods;
  }, [bankSetup, prefs, activeCard, lastUsedByMethod]);

  // Note: We intentionally do NOT force-switch defaults here.
  // The bank account row should appear even when card/zelle is default; the
  // fallback in the query above ensures the latest active ACH shows up.

  const upsertPrefs = useMutation({
    mutationFn: async (input: Partial<Pick<ParentPaymentPreferences, 'default_payment_method' | 'autopay_enabled'>> & { default_bank_account_setup_intent_id?: string | null }) => {
      const { data, error } = await supabase.rpc('upsert_parent_payment_preferences', {
        p_default_payment_method: input.default_payment_method ?? null,
        p_autopay_enabled: typeof input.autopay_enabled === 'boolean' ? input.autopay_enabled : null,
        p_default_bank_account_setup_intent_id: input.default_bank_account_setup_intent_id ?? null
      });
      if (error) throw error;
      return data as ParentPaymentPreferences;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-payment-prefs'] });
    }
  });

  const removeBank = useMutation({
    mutationFn: async () => {
      if (!bankSetup?.id) return;
      const { error } = await supabase
        .from('bank_account_setup_intents')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', bankSetup.id);
      if (error) throw error;

      if (
        prefs?.default_payment_method === 'bank_account' &&
        prefs.default_bank_account_setup_intent_id === bankSetup.id
      ) {
        const fallbackToZelle = !activeCard;
        await supabase.rpc('upsert_parent_payment_preferences', {
          p_default_payment_method: fallbackToZelle ? 'zelle' : 'card',
          p_autopay_enabled: fallbackToZelle ? false : null,
          p_default_bank_account_setup_intent_id: null,
        });
        if (fallbackToZelle) {
          setAutoPayEnabled(false);
          setAutoPayNotice('Auto Pay was turned off because Zelle does not support autopay.');
        }
      } else if (prefs?.default_bank_account_setup_intent_id === bankSetup.id) {
        // If bank wasn't the default, still clear the stored reference so the removed
        // (inactive) bank account doesn't show up in the UI.
        await supabase.rpc('upsert_parent_payment_preferences', {
          p_default_payment_method: prefs?.default_payment_method ?? null,
          p_autopay_enabled: null,
          p_default_bank_account_setup_intent_id: null,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-account-setup'] });
      queryClient.invalidateQueries({ queryKey: ['parent-payment-prefs'] });
      // Also directly reset any stale local-derived list by invalidating generic lists using parent id
      if (parent?.id) {
        queryClient.invalidateQueries({ queryKey: ['bank-account-setup', parent.id] });
        queryClient.invalidateQueries({ queryKey: ['parent-payment-prefs', parent.id] });
      }
    },
  });

  const handleRemoveBank = () => removeBank.mutate();

  // ── Inline profile edit (name + phone) ──────────────────────────────────
  // Email is intentionally never editable here: every parent-facing RLS
  // policy resolves identity via auth.jwt() ->> 'email' matched against
  // parents.email, and the "parents" policy has no explicit WITH CHECK (so
  // Postgres reuses USING) — an email change would fail the check at the
  // DB layer, and even if it succeeded it would orphan the parent from
  // their own students. Changing email requires a separate auth-identity
  // migration path.
  const [editingField, setEditingField] = useState<'name' | 'phone' | null>(null);
  const [draftFirstName, setDraftFirstName] = useState('');
  const [draftLastName, setDraftLastName] = useState('');
  const [draftPhone, setDraftPhone] = useState('');
  const [profileError, setProfileError] = useState<string | null>(null);

  const updateProfile = useMutation({
    mutationFn: async (input: { first_name?: string; last_name?: string; phone?: string }) => {
      if (!parent?.id) throw new Error('Parent not found');
      const { error } = await supabase.from('parents').update(input).eq('id', parent.id);
      if (error) throw error;
      return input;
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ['parent-id', user?.email] });
      const previous = queryClient.getQueryData(['parent-id', user?.email]);
      queryClient.setQueryData(['parent-id', user?.email], (old: typeof parent) =>
        old ? { ...old, ...input } : old
      );
      return { previous };
    },
    onError: (err: unknown, input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['parent-id', user?.email], context.previous);
      }
      const message = err instanceof Error ? err.message : 'Failed to save. Please try again.';
      setProfileError(message);
      trackEvent({
        eventType: 'parent_profile_update',
        user,
        userType: 'parent',
        entityType: 'parent',
        entityId: parent?.id,
        success: false,
        errorMessage: message,
        metadata: { location: 'account_page', fields: Object.keys(input) },
      });
    },
    onSuccess: (input) => {
      setProfileError(null);
      // Field names only — never the values. Same reasoning as the contact
      // editor: app_events is a broad-read audit log.
      trackEvent({
        eventType: 'parent_profile_update',
        user,
        userType: 'parent',
        entityType: 'parent',
        entityId: parent?.id,
        metadata: { location: 'account_page', fields: Object.keys(input) },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-id', user?.email] });
    },
  });

  const startEditName = () => {
    setDraftFirstName(parent?.first_name ?? '');
    setDraftLastName(parent?.last_name ?? '');
    setProfileError(null);
    setEditingField('name');
  };

  const startEditPhone = () => {
    setDraftPhone(parent?.phone ?? '');
    setProfileError(null);
    setEditingField('phone');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setProfileError(null);
  };

  const saveName = () => {
    const first_name = draftFirstName.trim();
    const last_name = draftLastName.trim();
    if (!first_name || !last_name) {
      setProfileError('First and last name are required.');
      return;
    }
    setEditingField(null);
    updateProfile.mutate({ first_name, last_name });
  };

  const savePhone = () => {
    // Store the canonical form: fetch-zelle-emails matches parents.phone
    // against the Zelle sender string exactly, so a raw "6505550100" here
    // would silently stop that parent's payments from auto-matching.
    if (!isValidPhoneOrEmpty(draftPhone)) {
      setProfileError('Enter a 10-digit phone number.');
      return;
    }
    const trimmed = draftPhone.trim();
    setEditingField(null);
    updateProfile.mutate({ phone: trimmed ? formatPhoneCanonical(trimmed) : '' });
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      trackEvent({
        eventType: 'user_signout',
        metadata: { location: 'account_page' }
      });
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      setIsSigningOut(false);
    }
  };

  const handleAutoPayToggle = () => {
    const next = !autoPayEnabled;
    const hasCapableBank = !!bankSetup && bankSetup.is_active === true && bankSetup.status === 'succeeded';
    const hasCard = !!activeCard;

    // Turning OFF autopay: keep existing default payment method unchanged.
    if (!next) {
      setAutoPayEnabled(false);
      upsertPrefs.mutate({ autopay_enabled: false });
      analytics.trackAutopayToggle({ user, enabled: false });
      return;
    }

    // Enabling autopay must NOT create prefs or guess a method.
    // Require an existing prefs row with an explicit, non-Zelle default.
    if (!prefs || !prefs.default_payment_method || prefs.default_payment_method === 'zelle') {
      setAutoPayEnabled(false);
      setAutoPayNotice(
        'Before enabling Auto Pay, please set your default payment method to a bank account or card.'
      );
      return;
    }

    if (prefs.default_payment_method === 'bank_account') {
      if (!hasCapableBank) {
        setAutoPayEnabled(false);
        setAutoPayNotice(
          'Auto Pay with bank account requires a verified bank connection. Please complete bank verification first.'
        );
        return;
      }
    } else if (prefs.default_payment_method === 'card') {
      if (!hasCard) {
        setAutoPayEnabled(false);
        setAutoPayNotice(
          'Auto Pay with card requires an active saved card. Please add or restore your card first.'
        );
        return;
      }
    }

    setAutoPayEnabled(true);
    upsertPrefs.mutate({ autopay_enabled: true });
    analytics.trackAutopayToggle({ user, enabled: true });
  };

  const handleAddPaymentMethod = () => {
    navigate('/account/add-payment-method');
    trackEvent({
      eventType: 'add_payment_method',
      user,
      metadata: { location: 'account_page' }
    });
  };

  const handleSetDefaultPayment = (methodId: string) => {
    const selected = paymentMethods.find(m => m.id === methodId);
    if (!selected) return;
    const method: 'card' | 'bank_account' | 'zelle' = selected.type === 'bank_account' ? 'bank_account' : selected.type === 'card' ? 'card' : 'zelle';

    // Guard: disallow setting ACH as default until the bank is verified (setup intent succeeded).
    if (method === 'bank_account' && selected.bankVerificationStatus !== 'verified') {
      setPaymentMethodNotice('Your bank account verification is still pending. Please finish verification before setting ACH as your default payment method.');
      return;
    }

    setPaymentMethodNotice(null);
    upsertPrefs.mutate({
      default_payment_method: method,
      default_bank_account_setup_intent_id: method === 'bank_account' ? selected.id : null,
      autopay_enabled: method === 'zelle' ? false : undefined,
    });
    if (method === 'zelle') {
      setAutoPayEnabled(false);
      setAutoPayNotice('Auto Pay was turned off because Zelle does not support autopay.');
    }
    analytics.trackSetDefaultPaymentMethod({ user, method });
  };

  const handleRemoveCard = async () => {
    if (!activeCard) return;
    try {
      const { error } = await supabase
        .from('card_payment_methods')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', activeCard.id);
      if (error) throw error;
      // If card was default, switch to bank if available, otherwise zelle
      if (prefs?.default_payment_method === 'card') {
        const fallbackToZelle = !bankSetup;
        await supabase.rpc('upsert_parent_payment_preferences', {
          p_default_payment_method: fallbackToZelle ? 'zelle' : 'bank_account',
          p_autopay_enabled: fallbackToZelle ? false : null,
          p_default_bank_account_setup_intent_id: bankSetup ? bankSetup.id : null,
        });
        if (fallbackToZelle) {
          setAutoPayEnabled(false);
          setAutoPayNotice('Auto Pay was turned off because Zelle does not support autopay.');
        }
      }
      queryClient.invalidateQueries({ queryKey: ['card-pm'] });
      queryClient.invalidateQueries({ queryKey: ['parent-payment-prefs'] });
    } catch (err) {
      console.error('Failed to remove saved card', err);
    }
  };

  const userFullName = user?.user_metadata?.full_name || 'User';
  const userEmail = user?.email || '';
  const userAvatar = user?.user_metadata?.avatar_url || user?.user_metadata?.picture;

  return (
    <div className="p-3 sm:p-4">


        {/* Account Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-gray-600" />
            {t('header.accountInformation')}
          </h2>
          
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              {userAvatar ? (
                <Img
                  src={userAvatar}
                  alt={userFullName}
                  className="h-16 w-16 rounded-full border-2 border-gray-200"
                  width={64}
                  height={64}
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-2 border-gray-200">
                  <User className="h-8 w-8 text-white" />
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              {editingField === 'name' ? (
                <div className="flex items-center gap-1.5" data-testid="account-name-edit">
                  <input
                    type="text"
                    value={draftFirstName}
                    onChange={e => setDraftFirstName(e.target.value)}
                    placeholder="First name"
                    autoFocus
                    className="w-24 sm:w-32 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={draftLastName}
                    onChange={e => setDraftLastName(e.target.value)}
                    placeholder="Last name"
                    className="w-24 sm:w-32 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') cancelEdit(); }}
                  />
                  <button onClick={saveName} aria-label="Save name" className="p-1 text-green-600 hover:bg-green-50 rounded">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={cancelEdit} aria-label="Cancel" className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 group">
                  <p className="text-base sm:text-lg font-medium text-gray-900 truncate">
                    {parent?.first_name ? `${parent.first_name} ${parent.last_name}` : userFullName}
                  </p>
                  {parent?.id && (
                    <button
                      onClick={startEditName}
                      aria-label="Edit name"
                      data-testid="account-edit-name-btn"
                      className="p-1 text-gray-300 hover:text-gray-600 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
              <div className="flex items-center mt-1">
                <Mail className="w-4 h-4 text-gray-400 mr-2" />
                <p className="text-xs sm:text-sm text-gray-600 truncate">{userEmail}</p>
              </div>
              {editingField === 'phone' ? (
                <div className="flex items-center gap-1.5 mt-1" data-testid="account-phone-edit">
                  <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                  <input
                    type="tel"
                    value={draftPhone}
                    onChange={e => setDraftPhone(handlePhoneInput(e.target.value))}
                    placeholder="(650) 555-0100"
                    autoFocus
                    className="w-36 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyDown={e => { if (e.key === 'Enter') savePhone(); if (e.key === 'Escape') cancelEdit(); }}
                  />
                  <button onClick={savePhone} aria-label="Save phone" className="p-1 text-green-600 hover:bg-green-50 rounded">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={cancelEdit} aria-label="Cancel" className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center mt-1 gap-1.5 group">
                  <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                  <p className="text-xs sm:text-sm text-gray-600">{parent?.phone || 'No phone number'}</p>
                  {parent?.id && (
                    <button
                      onClick={startEditPhone}
                      aria-label="Edit phone"
                      data-testid="account-edit-phone-btn"
                      className="p-1 text-gray-300 hover:text-gray-600 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
              {profileError && (
                <p className="text-xs text-red-600 mt-1">{profileError}</p>
              )}
              {parent?.id && <AccountAddressRow />}
              {isAdmin && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mt-2">
                  Administrator
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Payment Settings */}
        <div id="payment-settings" className="scroll-mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CreditCard className="w-5 h-5 mr-2 text-gray-600" />
            {t('header.paymentSettings')}
          </h2>
          
          <div className="space-y-6">
            {/* Auto Pay Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg mr-3">
                  <Repeat className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm sm:text-base font-medium text-gray-900">Auto Pay</p>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {autoPayEnabled 
                      ? 'Automatically pay invoices when due' 
                      : 'Manually pay invoices each month'
                    }
                  </p>
                  {!autoPayEnabled && !prefs && (
                    <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded mt-2 px-2 py-1 inline-block">
                      {autoPayNotice || 'Set a default payment method (bank account or card) to enable Auto Pay.'}
                    </p>
                  )}
                  {!autoPayEnabled && prefs?.default_payment_method === 'zelle' && (
                    <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded mt-2 px-2 py-1 inline-block">
                      {autoPayNotice || 'Auto Pay is not available for Zelle. Connect a bank account or card to enable Auto Pay.'}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={handleAutoPayToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  autoPayEnabled ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                data-testid="autopay-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoPayEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Payment Methods */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">{t('header.paymentMethods')}</h3>
                <button
                  onClick={handleAddPaymentMethod}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add New
                </button>
              </div>

              {paymentMethodNotice && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded mb-3 px-3 py-2">
                  {paymentMethodNotice}
                </p>
              )}

              {/* Chargeable methods: one card, hairline dividers between rows */}
              <div className="rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
                {paymentMethods.map((method) => {
                  const isBankPending = method.type === 'bank_account' && method.bankVerificationStatus !== 'verified';
                  const canRemove =
                    (method.type === 'card' && !!activeCard) ||
                    (method.type === 'bank_account' && !!bankSetup);
                  const hasMenu = !method.isDefault || canRemove;
                  const menuOpen = openMenuId === method.id;
                  return (
                    <PaymentMethodRow
                      key={method.id}
                      method={method}
                      actions={
                        <>
                            {hasMenu && (
                              <button
                                onClick={() => setOpenMenuId(menuOpen ? null : method.id)}
                                aria-label={`Options for ${method.name}`}
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                data-testid={`pm-menu-${method.type}`}
                                className={`p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors ${
                                  menuOpen ? 'bg-gray-100 text-gray-600' : ''
                                }`}
                              >
                                <MoreHorizontal className="w-5 h-5" />
                              </button>
                            )}

                            {menuOpen && (
                              <>
                                {/* Click-away backdrop */}
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setOpenMenuId(null)}
                                  aria-hidden="true"
                                />
                                <div
                                  role="menu"
                                  className="absolute right-3 top-12 z-20 w-48 rounded-xl border border-gray-200 bg-white shadow-lg py-1 overflow-hidden"
                                >
                                  {!method.isDefault && (
                                    <button
                                      role="menuitem"
                                      onClick={() => {
                                        if (isBankPending) return;
                                        handleSetDefaultPayment(method.id);
                                        setOpenMenuId(null);
                                      }}
                                      disabled={isBankPending}
                                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left ${
                                        isBankPending
                                          ? 'text-gray-400 cursor-not-allowed'
                                          : 'text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      <Star className="w-4 h-4" />
                                      Set Default
                                    </button>
                                  )}
                                  {isBankPending && (
                                    <p className="px-3 py-1.5 text-xs text-gray-400">
                                      Finish verification first
                                    </p>
                                  )}
                                  {method.type === 'card' && activeCard && (
                                    <button
                                      role="menuitem"
                                      onClick={() => {
                                        handleRemoveCard();
                                        setOpenMenuId(null);
                                      }}
                                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Remove
                                    </button>
                                  )}
                                  {method.type === 'bank_account' && bankSetup && (
                                    <button
                                      role="menuitem"
                                      onClick={() => {
                                        handleRemoveBank();
                                        setOpenMenuId(null);
                                      }}
                                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </>
                            )}
                        </>
                      }
                    />
                  );
                })}
              </div>

              {/* BHCS balance & rewards — distinct from chargeable methods */}
              <h3 className="text-sm font-semibold text-gray-900 mt-6 mb-3">{t('header.balanceRewards')}</h3>
              <div className="rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
                <Link
                  to="/wallet"
                  className="flex items-center justify-between px-4 py-3.5 bg-white hover:bg-gray-50/70 transition-colors"
                  data-testid="account-bhcs-cash-link"
                >
                  <div className="flex items-center">
                    <div className="p-2 rounded-lg mr-3 bg-blue-100">
                      <CurrencyDollarIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-900">BHCS Cash</p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        View balance, autopay settings &amp; activity
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>

                <Link
                  to="/coupons"
                  className="flex items-center justify-between px-4 py-3.5 bg-white hover:bg-gray-50/70 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="p-2 rounded-lg mr-3 bg-green-100">
                      <Tag className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium text-gray-900">Coupons</p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        Check available discounts and offers
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">{t('header.quickActions')}</h2>
          
          <div className="space-y-3">
            {/* FAQ Link */}
            <Link
              to="/faq"
              className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <HelpCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm sm:text-base font-medium text-gray-900">Help & FAQ</p>
                  <p className="text-xs sm:text-sm text-gray-600">Find answers to common questions</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
            </Link>

            {/* Sign Out */}
            <button
              onClick={handleSignOut}
              disabled={isSigningOut}
              className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-red-50 hover:border-red-200 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg mr-3">
                  <LogOut className="w-5 h-5 text-red-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm sm:text-base font-medium text-gray-900">Sign Out</p>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {isSigningOut ? 'Signing out...' : 'Sign out of your account'}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors" />
            </button>
          </div>
        </div>

        {/* About BHCS Portal */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <button
            onClick={() => setIsAboutCollapsed(!isAboutCollapsed)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg mr-3">
                <Info className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('header.aboutPortal')}</h2>
                <p className="text-xs sm:text-sm text-gray-600">Version information and support</p>
              </div>
            </div>
            <div className="flex items-center">
              {isAboutCollapsed ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400 transform rotate-180" />
              )}
            </div>
          </button>
          
          {!isAboutCollapsed && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-600">Version</span>
                  <span className="text-xs sm:text-sm font-medium text-gray-900">{VERSION_INFO.display}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-600">Last Updated</span>
                  <span className="text-xs sm:text-sm font-medium text-gray-900">
                    {formatDateInBusinessTZ(`${VERSION_INFO.releasedOn}`)}
                  </span>
                </div>
                <div className="pt-2">
                  <p className="text-xs sm:text-sm text-gray-600 mb-2">
                    Need help? Contact our support team:
                  </p>
                  <a 
                    href="mailto:billing@bhcskids.com" 
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    billing@bhcskids.com
                  </a>
                </div>
                
                {/* Legal Links */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                    <span className="text-xs text-gray-500">
                      © {new Date().getFullYear()} {LEGAL_ENTITY_NAME}
                    </span>
                    <div className="flex space-x-4">
                      <Link 
                        to="/privacy-policy" 
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        Privacy Policy
                      </Link>
                      <Link 
                        to="/terms-of-service" 
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        Terms of Service
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
    </div>
  );
} 